import * as React from 'react';

import { getProductGridViewState } from 'components/molecules/ProductGridState';
import type { ProductCard } from 'libs/apis/generated/models/ProductCard';
import { fetchWishlistProducts } from 'libs/apis/services/FavoriteProductsService';
import useLoadMoreObserver from 'libs/hooks/useLoadMoreObserver';

export type WishlistProductsState = {
  items: ProductCard[];
  status: 'idle' | 'loading' | 'succeeded' | 'error';
  error: string | null;
  nextPage: number | null;
};

type UseWishlistProductsParams = {
  token: string;
};

type UseWishlistProductsResult = {
  state: WishlistProductsState;
  viewState: ReturnType<typeof getProductGridViewState>;
  loadMoreTriggerRef: React.RefObject<HTMLDivElement>;
};

const useWishlistProducts = ({ token }: UseWishlistProductsParams): UseWishlistProductsResult => {
  const [state, setState] = React.useState<WishlistProductsState>({
    items: [],
    status: 'idle',
    error: null,
    nextPage: null,
  });

  const loadProducts = React.useCallback(
    async ({ append, page }: { append?: boolean; page?: number } = {}) => {
      const targetPage = page ?? 1;
      if (append && !page) {
        return;
      }

      setState((prev) => ({
        ...prev,
        status: 'loading',
        error: null,
      }));

      const result = await fetchWishlistProducts({
        token,
        page: targetPage,
      });

      if (result.isSuccess()) {
        const response = result.value;
        setState((prev) => ({
          items: append ? [...prev.items, ...response.products] : response.products,
          status: 'succeeded',
          error: null,
          nextPage: response.nextPage ?? null,
        }));
        return;
      }

      setState((prev) => ({
        ...prev,
        status: 'error',
        error: '商品情報の取得に失敗しました。\n時間をおいて再度お試しください。',
      }));
    },
    [token]
  );

  React.useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleIntersect = React.useCallback(() => {
    if (!state.nextPage) {
      return;
    }
    loadProducts({ append: true, page: state.nextPage });
  }, [loadProducts, state.nextPage]);

  const loadMoreTriggerRef = useLoadMoreObserver({
    isEnabled: Boolean(state.nextPage) && state.status !== 'loading',
    onIntersect: handleIntersect,
  });

  const viewState = getProductGridViewState(state.status, state.items.length);

  return {
    state,
    viewState,
    loadMoreTriggerRef,
  };
};

export default useWishlistProducts;
